<template>
  <router-view></router-view>
</template>

<script>
import { setThemeName } from '@/plugins/vuetify.js';
import loadConfigByThemeId, {
  defaultThemeName,
  getThemeIdFromStateAndRoute
} from '@/config/themes.js';
import {
  SMALLEST_BREAKPOINT,
  MIDDLE_BREAKPOINT,
  LARGEST_BREAKPOINT
} from '@/stores/constants.js';
import { mapActions, mapState } from 'pinia';
import { useResponsiveBreakpointsStore } from '@/stores/responsiveBreakpoints.js';
import { useThemeStore } from '@/stores/theme.js';
import { useTrackingDataStore } from '@/stores/trackingData.js';
import { sendAnalytics } from '@/services/analytics.js';

export default {
  name: 'App',
  data() {
    return {
      isResizing: false,
      resizeStartTime: null,
      resizingDelta: 0
    };
  },
  computed: {
    ...mapState(useThemeStore, ['themeId']),
    ...mapState(useTrackingDataStore, {
      trackingData: 'data'
    }),
    themeConfig() {
      return loadConfigByThemeId(this.themeId);
    }
  },
  methods: {
    ...mapActions(useThemeStore, ['setThemeId']),
    ...mapActions(useResponsiveBreakpointsStore, [
      'setIsBelowSmallestBreakpoint',
      'setIsBelowMiddleBreakpoint',
      'setIsBelowLargestBreakpoint'
    ]),
    updateTheme() {
      const newThemeId =
        getThemeIdFromStateAndRoute(this.trackingData, this.$route) ||
        defaultThemeName();

      setThemeName(this.$vuetify, newThemeId);
      this.setThemeId(newThemeId);
    },
    resizeHandler() {
      this.resizeStartTime = new Date();
      if (!this.isResizing) {
        this.isResizing = true;
        setTimeout(this.resizeEnd, this.resizingDelta);
      }
    },
    resizeEnd() {
      if (new Date() - this.resizeStartTime < this.resizingDelta) {
        setTimeout(this.resizeEnd, this.resizingDelta);
      } else {
        this.isResizing = false;
        const isBelowSmallestBreakpoint =
          window.innerWidth < SMALLEST_BREAKPOINT;
        this.setIsBelowSmallestBreakpoint(isBelowSmallestBreakpoint);
        const isBelowMiddleBreakpoint = window.innerWidth < MIDDLE_BREAKPOINT;
        this.setIsBelowMiddleBreakpoint(isBelowMiddleBreakpoint);
        const isBelowLargestBreakpoint = window.innerWidth < LARGEST_BREAKPOINT;
        this.setIsBelowLargestBreakpoint(isBelowLargestBreakpoint);
      }
    },
    errorHandler(errorEvent) {
      if (!this.trackingData.tenantId) return;
      sendAnalytics(
        {
          trackingId: '',
          parcelNumber: '',
          ...this.trackingData
        },
        'global',
        'error',
        {
          message: errorEvent.message,
          position: `${errorEvent.filename}:${errorEvent.lineno}:${errorEvent.colno}`,
          userAgent: navigator.userAgent || navigator.vendor || window.opera
        }
      );
    }
  },
  created() {
    window.addEventListener('resize', this.resizeHandler);
    window.addEventListener('error', this.errorHandler);
  },
  mounted() {
    if (this.$rttI18n.$isWidget) return;
    const trackingDataStore = useTrackingDataStore();
    trackingDataStore.$subscribe((mutation, state) => {
      this.updateTheme();
    });
  },
  unmounted() {
    window.removeEventListener('resize', this.resizeHandler);
    window.removeEventListener('error', this.errorHandler);
  },
  watch: {
    '$route.params.countryOrTenantId'(newCountry, oldCountry) {
      if (this.$rttI18n.$isWidget) return;
      this.updateTheme();
    }
  }
};
</script>

<style></style>
